import type { ReactNode } from 'react';
import type { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import type { i18n as I18nInstance } from 'i18next';
import { I18nextProvider } from 'react-i18next';
import { Provider as ReduxProvider } from 'react-redux';

import { DynamicThemeProvider } from '@/app/DynamicThemeProvider';
import type { AppStore } from '@/app/store';
import { SeoContext, type SeoCollector } from '@/seo/seo';

interface AppProvidersProps {
  store: AppStore;
  i18n: I18nInstance;
  emotionCache: EmotionCache;
  /** Supplied only during prerendering, where effects never run. */
  seoCollector?: SeoCollector;
  children: ReactNode;
}

/**
 * The provider stack, shared verbatim by the browser entry and the prerenderer
 * so that server output and hydrated output cannot drift apart.
 */
export function AppProviders({
  store,
  i18n,
  emotionCache,
  seoCollector,
  children,
}: AppProvidersProps) {
  return (
    <CacheProvider value={emotionCache}>
      <ReduxProvider store={store}>
        <I18nextProvider i18n={i18n}>
          <DynamicThemeProvider>
            <CssBaseline />
            <SeoContext.Provider value={seoCollector ?? null}>{children}</SeoContext.Provider>
          </DynamicThemeProvider>
        </I18nextProvider>
      </ReduxProvider>
    </CacheProvider>
  );
}

export default AppProviders;
